"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { Map as LeafletMap } from "leaflet";

// Leaflet's own stylesheet positions the tile grid and the zoom/attribution controls. Without
// it the tiles stack vertically as unpositioned images and the map looks broken rather than
// unstyled. Next allows importing CSS from `node_modules` in any client component.
import "leaflet/dist/leaflet.css";

import type { GymDetail } from "@/types";

/**
 * The network map — every partner gym as a pin, after DESIGN.md §322's standing note that a map
 * was "impossible" without coordinates. It is possible now: Odoo owns each gym's
 * `partner_latitude`/`partner_longitude`, and the public API joins them at read time.
 *
 * Leaflet directly rather than `react-leaflet`: the whole surface used here is tiles, markers,
 * labels and a `fitBounds`, so the wrapper would add a second dependency and a React-version
 * peer constraint to save a `useEffect`.
 */

/** Kuwait, for the degenerate case where not one gym has been located yet. */
const KUWAIT_FALLBACK: [number, number] = [29.3759, 47.9774];
const FALLBACK_ZOOM = 10;

/**
 * Never zoom past this on "show all".
 *
 * Lowered from 14 to 12: with two gyms a few streets apart, 14 filled the frame with one
 * neighbourhood and no coastline, so the map opened with no recognisable landmark to orient
 * against. 12 keeps enough of Kuwait in frame to know where you are.
 */
const MAX_FIT_ZOOM = 12;

/**
 * Above this many pins, names are shown on hover instead of permanently.
 *
 * Permanent labels are what make the map readable at a glance — you can see WHICH gyms these
 * are without clicking each dot. But they overlap once the estate is dense, at which point an
 * unreadable pile of labels is worse than none.
 */
const MAX_PERMANENT_LABELS = 12;

type PinnedGym = GymDetail & { latitude: number; longitude: number };

/**
 * 🔴 The single rule this whole feature rests on: a gym is plotted only if it has BOTH
 * coordinates. Null means nobody has located it in Odoo — an ordinary state for a real,
 * published gym — and the number `0` is a genuine coordinate in the Gulf of Guinea, so a
 * `?? 0` fallback anywhere here would put a marker 4,000km off the African coast.
 *
 * `Number.isFinite` and not a truth test: `0` is falsy but valid, and the backend's Null Island
 * rule has already mapped a real "unset" to `null` before it reaches us.
 */
function isPinned(gym: GymDetail): gym is PinnedGym {
  return Number.isFinite(gym.latitude) && Number.isFinite(gym.longitude);
}

export function GymMap({
  gyms,
  label,
  height = 420,
}: {
  gyms: GymDetail[];
  label: string;
  /** Pixel height. The directory's network map is tall; a single-gym sidebar map is not. */
  height?: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  /** Re-fits the view to every pin. Held in a ref so the toolbar can call into the effect. */
  const showAllRef = useRef<() => void>(() => {});
  const [isFullscreen, setIsFullscreen] = useState(false);

  const pinned = gyms.filter(isPinned);
  const pinnedCount = pinned.length;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    // Dynamic import, not a top-level one: Leaflet touches `window` at module scope, which
    // throws during Next's server render of this route even though the component is a client
    // one. Importing inside the effect guarantees it only ever loads in the browser.
    void import("leaflet").then((L) => {
      if (cancelled || mapRef.current) return;

      const map = L.map(container, {
        // Wheel-zoom ON (changed 2026-08-07 at the owner's request). The trade-off is real and
        // known: the wheel now zooms the map instead of scrolling the page whenever the pointer
        // is over it. `zoomSnap`/`wheelPxPerZoomLevel` are tuned below so a normal wheel flick
        // is a gentle step rather than a jump across four zoom levels, which is what makes
        // scroll-zoom feel uncontrollable on most embedded maps.
        scrollWheelZoom: true,
        // Fractional zoom steps — smooth, and far less jarring than snapping a whole level.
        zoomSnap: 0.25,
        zoomDelta: 0.5,
        // Higher = more wheel travel per zoom level = finer control. Leaflet's default (60) is
        // twice as twitchy as this.
        wheelPxPerZoomLevel: 140,
      });
      mapRef.current = map;

      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        // Required by the OSM tile usage policy — these tiles are free, not unattributed.
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(map);

      // 🔴 A `divIcon`, NOT Leaflet's default marker. The default resolves its PNGs relative to
      // the CSS file, which every bundler rewrites — the markers then 404 and the map renders
      // with no pins at all, looking exactly like "no gyms have coordinates". A CSS-only pin
      // has no asset to lose, and it can use the site's own signal colour.
      //
      // A teardrop, not the 14px dot this started as: a dot reads as a map artefact (a park, a
      // roundabout) rather than as something placed, and there was no way to tell one from a
      // feature of the tiles underneath.
      const icon = L.divIcon({
        className: "xf-pin",
        html:
          '<span style="display:block;width:22px;height:22px;border-radius:9999px 9999px 9999px 2px;' +
          "transform:rotate(-45deg);background:var(--xf-signal);" +
          'border:2px solid var(--xf-signal-fg);box-shadow:0 2px 6px rgba(0,0,0,.45)"></span>',
        iconSize: [22, 22],
        // Anchored at the tip of the teardrop (bottom-left before rotation), so the pin points
        // at its coordinate instead of hovering beside it.
        iconAnchor: [11, 22],
        popupAnchor: [0, -22],
        tooltipAnchor: [0, -12],
      });

      for (const gym of pinned) {
        const marker = L.marker([gym.latitude, gym.longitude], {
          icon,
          // Native browser tooltip, and the accessible name for the marker's button role.
          title: gym.name,
          alt: gym.name,
          riseOnHover: true,
        }).addTo(map);

        // The single biggest readability win: the map now NAMES its pins instead of making you
        // click each one to find out what it is. Permanent while the estate is small; on hover
        // once labels would start colliding.
        marker.bindTooltip(gym.name, {
          permanent: pinnedCount <= MAX_PERMANENT_LABELS,
          direction: "top",
          className: "xf-pin-label",
          offset: [0, -4],
        });

        // `escapeHtml` on the name: this string is authored in Odoo, and Leaflet's popup takes
        // raw HTML. A gym called `<img onerror=...>` must render as text, not run.
        marker.bindPopup(
          `<strong>${escapeHtml(gym.name)}</strong><br>${escapeHtml(gym.area)}<br>` +
            `<a href="/gyms/${encodeURIComponent(gym.slug)}">View gym</a>`,
        );
      }

      const showAll = () => {
        if (pinned.length === 0) {
          // Every gym unpinned. Showing Kuwait beats showing the whole globe centred on 0,0 —
          // which is what Leaflet does with no view set, and which looks like a broken map.
          map.setView(KUWAIT_FALLBACK, FALLBACK_ZOOM);
          return;
        }
        map.fitBounds(
          pinned.map((gym) => [gym.latitude, gym.longitude] as [number, number]),
          { padding: [56, 56], maxZoom: MAX_FIT_ZOOM },
        );
      };
      showAllRef.current = showAll;
      showAll();
    });

    return () => {
      cancelled = true;
      // Leaflet keeps DOM listeners and a `_leaflet_id` on the container; without `remove()`
      // a re-mount (any filter change re-renders this tree) throws "Map container is already
      // initialized" and the map never comes back.
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // `pinnedCount` drives the permanent-vs-hover label decision, so it belongs in the deps.
  }, [gyms, pinned, pinnedCount]);

  // Fullscreen is where the map stops being a thumbnail and becomes explorable — the real fix
  // for "hard to navigate" on a 420px letterbox.
  const toggleFullscreen = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void frame.requestFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
      // See the ResizeObserver below — entering fullscreen is just the largest resize.
      requestAnimationFrame(() => mapRef.current?.invalidateSize());
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // 🔴 Leaflet caches the container's pixel size when the map is created and never re-reads it
  // on its own. Any resize afterwards — window drag, browser page zoom, phone rotation, a
  // sidebar opening — leaves it painting the OLD tile grid inside the NEW box, so the map
  // renders correctly in one corner with dead grey filling the rest.
  //
  // Found by accident: browser-zooming this page grew the container and left ~40% of it grey.
  // A `window.resize` listener does NOT cover it — page zoom and layout-driven resizes don't
  // always fire one — so this observes the element itself.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      // rAF so Leaflet measures after the browser has settled the new layout, not during it.
      requestAnimationFrame(() => mapRef.current?.invalidateSize());
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return (
    <div>
      <div ref={frameRef} className="relative bg-surface">
        <div
          ref={containerRef}
          role="application"
          aria-label={label}
          // Inline height, not a Tailwind class: an arbitrary-value class built from a prop
          // (`h-[${height}px]`) is invisible to Tailwind's static scanner and would be purged
          // from the stylesheet, collapsing the map to zero height. In fullscreen the frame
          // owns the height, so the fixed value has to give way.
          style={{ height: isFullscreen ? "100%" : height }}
          className="w-full overflow-hidden rounded-lg border border-edge bg-surface"
        />

        {/* Above Leaflet's own panes (z-index 400) and its controls (1000). */}
        <div className="pointer-events-none absolute right-3 top-3 z-[1100] flex gap-2">
          <button
            type="button"
            onClick={() => showAllRef.current()}
            className="pointer-events-auto rounded-md border border-edge bg-ground/95 px-3 py-1.5 text-micro font-medium uppercase tracking-wide text-ink-2 shadow-sm transition-colors hover:text-signal"
          >
            Show all
          </button>
          <button
            type="button"
            onClick={toggleFullscreen}
            className="pointer-events-auto rounded-md border border-edge bg-ground/95 px-3 py-1.5 text-micro font-medium uppercase tracking-wide text-ink-2 shadow-sm transition-colors hover:text-signal"
          >
            {isFullscreen ? "Exit" : "Fullscreen"}
          </button>
        </div>
      </div>

      {/* The map cannot show what it does not have, so the count says so plainly rather than
          letting a visitor infer the network is smaller than it is. */}
      {pinnedCount < gyms.length && (
        <p className="mt-2 font-mono text-micro uppercase text-muted" data-numeric>
          {`${pinnedCount} of ${gyms.length} shown — the rest are not mapped yet`}
        </p>
      )}
    </div>
  );
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
